/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import { Namespaces } from '../../../i18n';
import { useTableStyles } from '../../../styles/table';
import { IProjectWithClusters } from '../../../types/project/IProjectWithClusters';
import { useOrganizationPermissions } from '../../../utils/useOrganizationPermissions';
import { AlignCenter } from '../../AlignCenter/AlignCenter';
import { ServiceIcon } from '../../ServiceIcon';
import { createClustersListStyles } from './OrganizationClustersList.styles';

type ClustersListProps = {
  projectsClusters: IProjectWithClusters[];
  onManageClick: (project: IProjectWithClusters, cluster: IProjectWithClusters['clusters'][number]) => void;
};

export const OrganizationManageClustersList = memo<ClustersListProps>(props => {
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const classes = createClustersListStyles();
  const tableClasses = useTableStyles();

  const { projectsClusters, onManageClick } = props;

  const onManageRowClick = onManageClick ?? null;

  const clusters = useMemo(() => projectsClusters.flatMap(project => project.clusters), [projectsClusters]);

  const params = useParams<{ organization_id: string }>();
  const organizationId = params.organization_id!;
  const permissions = useOrganizationPermissions(organizationId);

  return (
    <TableContainer>
      <Table aria-label="clusters table" className={classes.root}>
        <TableHead>
          <TableRow>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('management.clusters_list.title_cluster')}
            </TableCell>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('management.clusters_list.title_service')}
            </TableCell>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('management.clusters_list.title_status')}
            </TableCell>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('management.clusters_list.title_project')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projectsClusters.map(project =>
            project.clusters.map(cluster => (
              <TableRow key={cluster.id}>
                <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                  <Typography component="p" className={tableClasses.bodyTableCellText}>
                    {cluster.name}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                  <ServiceIcon serviceType={cluster.type} variant="minimal" />
                </TableCell>
                <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                  <Typography component="p" className={tableClasses.bodyTableCellText}>
                    {cluster.status}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                  <Typography component="p" className={tableClasses.bodyTableCellText}>
                    {project.name}
                  </Typography>
                </TableCell>
                {onManageRowClick && permissions.edit_organization && (
                  <TableCell
                    classes={{
                      root: cx(classes.editColumn, tableClasses.bodyTableCell),
                    }}
                  >
                    <div
                      aria-label={t(`${Namespaces.Common}:label.manage_clust`, { id: cluster.id })}
                      className={classes.editIcon}
                      onClick={() => onManageRowClick(project, cluster)}
                      data-cy="clusters-tab-cluster-settings-button"
                    />
                  </TableCell>
                )}
              </TableRow>
            )),
          )}
          {!clusters.length && (
            <TableRow>
              <TableCell colSpan={4} classes={{ root: tableClasses.bodyTableCell }}>
                <AlignCenter>
                  <Typography component="p" className={tableClasses.bodyTableCellText}>
                    {t('management.clusters_list.no_clusters')}
                  </Typography>
                </AlignCenter>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
